import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import {
  bulkCreateProfiles, SCHEME_LABEL,
  type BrowserScheme, type BrowserWorkspace, type BulkProfileInput,
} from '@/api/browserProfiles';
import {
  CREDS_FORMATS, parseCreds, parseProxies, profileNameFromLogin,
} from '@/lib/credsImport';

interface Props {
  open: boolean;
  workspace: BrowserWorkspace | null;
  onClose: () => void;
  onImported: (count: number) => void;
}

const inputCls = 'w-full px-3 py-2 rounded-md bg-background border border-border text-content text-sm';
const areaCls = 'w-full px-3 py-2 rounded-md bg-background border border-border text-content font-mono text-xs h-40';

// A file input that reads the picked text file into a textarea's state.
function FromFile({ onText }: { onText: (t: string) => void }) {
  return (
    <label className="shrink-0 text-xs text-sky-400 hover:underline cursor-pointer">
      from file
      <input
        type="file"
        accept=".txt,.csv,text/plain,text/csv"
        className="hidden"
        onChange={async (e) => {
          const f = e.target.files?.[0];
          if (f) onText(await f.text());
          e.target.value = '';
        }}
      />
    </label>
  );
}

// Two-step bulk import: parse a creds file into accounts, then pair each with a
// proxy from a pasted list and create the profiles. The runner picks them up
// and creates each ShardX profile on its proxy, exactly as a one-off add does.
export function BrowserProfileImport({ open, workspace, onClose, onImported }: Props) {
  const qc = useQueryClient();
  const [step, setStep] = useState<'creds' | 'proxies'>('creds');
  const [formatId, setFormatId] = useState(CREDS_FORMATS[0]!.id);
  const [scheme, setScheme] = useState<BrowserScheme>('none');
  const [credsText, setCredsText] = useState('');
  const [proxyText, setProxyText] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setStep('creds'); setFormatId(CREDS_FORMATS[0]!.id); setScheme('none');
    setCredsText(''); setProxyText(''); setError(null);
  }, [open]);

  const format = CREDS_FORMATS.find((f) => f.id === formatId)!;
  const parsed = useMemo(() => parseCreds(formatId, credsText), [formatId, credsText]);
  const proxies = useMemo(() => parseProxies(proxyText), [proxyText]);
  const paired = Math.min(parsed.accounts.length, proxies.length);

  const rows = (): BulkProfileInput[] =>
    parsed.accounts.slice(0, proxies.length).map((a, i) => {
      const note = [a.order_id ? `Order ${a.order_id}` : '', !a.totp_seed && a.totp_raw ? `2FA: ${a.totp_raw}` : '']
        .filter(Boolean).join(' · ');
      return {
        name: profileNameFromLogin(a.login),
        google_email: a.login,
        password: a.password,
        recovery_email: a.recovery_email,
        totp_seed: a.totp_seed,
        app_password: a.app_password,
        proxy: proxies[i]!,
        ...(note ? { notes: note } : {}),
      };
    });

  const importMut = useMutation({
    mutationFn: () => bulkCreateProfiles({ workspace_id: workspace!.id, scheme, profiles: rows() }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['browser-profiles'] });
      qc.invalidateQueries({ queryKey: ['browser-workspaces'] });
      onImported(r.created);
      onClose();
    },
    onError: (e: any) => setError(e?.message ?? 'Import failed'),
  });

  const twoFa = (a: typeof parsed.accounts[number]) =>
    a.totp_seed ? <span className="text-green-400">secret</span>
      : a.totp_raw ? <span className="text-amber-400" title={a.totp_raw}>link, no seed</span>
      : <span className="text-content-muted">—</span>;

  return (
    <Modal isOpen={open} onClose={onClose} title={`Import profiles${workspace ? ` into ${workspace.name}` : ''}`} size="3xl">
      {error && (
        <div className="mb-3 px-3 py-2 rounded-md bg-red-500/10 border border-red-500/30 text-sm text-red-400">{error}</div>
      )}

      {step === 'creds' && (
        <>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-sm text-content-muted mb-1">Format</label>
              <select className={inputCls} value={formatId} onChange={(e) => setFormatId(e.target.value)}>
                {CREDS_FORMATS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm text-content-muted mb-1">Scheme for all</label>
              <select className={inputCls} value={scheme} onChange={(e) => setScheme(e.target.value as BrowserScheme)}>
                {(Object.keys(SCHEME_LABEL) as BrowserScheme[]).map((s) => <option key={s} value={s}>{SCHEME_LABEL[s]}</option>)}
              </select>
            </div>
          </div>
          <p className="text-xs text-content-muted mb-2">{format.hint}</p>

          <div className="flex items-center justify-between mb-1">
            <label className="block text-sm text-content-muted">Creds file</label>
            <FromFile onText={setCredsText} />
          </div>
          <textarea
            className={areaCls}
            value={credsText}
            onChange={(e) => setCredsText(e.target.value)}
            placeholder={'Order ID: 1791805960089571\nme@gmail.com:password:recovery@mail.com:JBSWY3DPEHPK3PXP:apppasswordxyz'}
          />

          <div className="mt-3 text-sm">
            <span className="text-content">{parsed.accounts.length} account{parsed.accounts.length === 1 ? '' : 's'} parsed</span>
            {parsed.skipped.length > 0 && (
              <span className="text-amber-400"> · {parsed.skipped.length} line{parsed.skipped.length === 1 ? '' : 's'} skipped</span>
            )}
          </div>

          {parsed.accounts.length > 0 && (
            <div className="mt-2 max-h-56 overflow-auto border border-border rounded-md">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-surface text-content-muted">
                  <tr>
                    <th className="text-left px-2 py-1 font-medium">Name</th>
                    <th className="text-left px-2 py-1 font-medium">Google account</th>
                    <th className="text-left px-2 py-1 font-medium">Recovery</th>
                    <th className="text-left px-2 py-1 font-medium">Password</th>
                    <th className="text-left px-2 py-1 font-medium">2FA</th>
                    <th className="text-left px-2 py-1 font-medium">App pw</th>
                    <th className="text-left px-2 py-1 font-medium">Order</th>
                  </tr>
                </thead>
                <tbody>
                  {parsed.accounts.slice(0, 50).map((a) => (
                    <tr key={a.line} className="border-t border-border">
                      <td className="px-2 py-1 text-content">{profileNameFromLogin(a.login)}</td>
                      <td className="px-2 py-1 text-content-muted">{a.login}</td>
                      <td className="px-2 py-1 text-content-muted">{a.recovery_email || '—'}</td>
                      <td className="px-2 py-1">{a.password ? <span className="text-green-400">✓</span> : <span className="text-red-400">missing</span>}</td>
                      <td className="px-2 py-1">{twoFa(a)}</td>
                      <td className="px-2 py-1 text-content-muted">{a.app_password ? '✓' : '—'}</td>
                      <td className="px-2 py-1 text-content-muted">{a.order_id || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {parsed.accounts.length > 50 && (
                <div className="px-2 py-1 text-xs text-content-muted border-t border-border">…and {parsed.accounts.length - 50} more</div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2 mt-4">
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
            <Button disabled={parsed.accounts.length === 0} onClick={() => { setError(null); setStep('proxies'); }}>
              Next: proxies
            </Button>
          </div>
        </>
      )}

      {step === 'proxies' && (
        <>
          <p className="text-sm text-content-muted mb-3">
            Paste <span className="text-content">{parsed.accounts.length}</span> prox{parsed.accounts.length === 1 ? 'y' : 'ies'}, one
            per line, in the order they should attach. <span className="font-mono text-xs">host:port:user:pass</span> or a full
            <span className="font-mono text-xs"> socks5://user:pass@host:port</span> — each account takes the next one on the list.
          </p>

          <div className="flex items-center justify-between mb-1">
            <label className="block text-sm text-content-muted">Proxies</label>
            <FromFile onText={setProxyText} />
          </div>
          <textarea
            className={areaCls}
            value={proxyText}
            onChange={(e) => setProxyText(e.target.value)}
            placeholder={'premium.proxywing.com:12345:wrzk1oy2l3:qs47rzh5rn_country-pl_session-0CzIycPE_lifetime-168h'}
          />

          <div className="mt-3 text-sm">
            <span className="text-content">{proxies.length} prox{proxies.length === 1 ? 'y' : 'ies'}</span>
            {' · '}
            {paired === parsed.accounts.length
              ? <span className="text-green-400">pairs with all {parsed.accounts.length} account{parsed.accounts.length === 1 ? '' : 's'}</span>
              : proxies.length < parsed.accounts.length
                ? <span className="text-amber-400">{parsed.accounts.length - proxies.length} account{parsed.accounts.length - proxies.length === 1 ? '' : 's'} left without a proxy — only {paired} will be created</span>
                : <span className="text-amber-400">{proxies.length - parsed.accounts.length} extra proxy line{proxies.length - parsed.accounts.length === 1 ? '' : 's'} ignored</span>}
          </div>

          <div className="flex justify-between gap-2 mt-4">
            <Button variant="secondary" onClick={() => { setError(null); setStep('creds'); }}>Back</Button>
            <div className="flex gap-2">
              <Button variant="secondary" onClick={onClose}>Cancel</Button>
              <Button loading={importMut.isPending} disabled={paired === 0 || !workspace}
                onClick={() => { setError(null); importMut.mutate(); }}>
                Create {paired} profile{paired === 1 ? '' : 's'}
              </Button>
            </div>
          </div>
        </>
      )}
    </Modal>
  );
}
